import React, { useState } from 'react';
import { Alert, AlertTitle, Box, Button, Collapse, Typography } from '@mui/material';

const DOCS_BASE_URL =
  (typeof process !== 'undefined' && (process as any).env?.REACT_APP_DOCS_BASE_URL) ||
  'https://github.com/ashsyal/edgeai/blob/main/docs';

export function buildDocsUrl(relativePath: string, anchor?: string): string {
  const cleanPath = String(relativePath || '').replace(/^\/+/, '');
  return `${DOCS_BASE_URL}/${cleanPath}${anchor ? `#${anchor}` : ''}`;
}

export interface PlaybookCalloutProps {
  title: string;
  doItems: string[];
  dontItems: string[];
  href?: string;
  linkLabel?: string;
  summary?: string;
  compact?: boolean;
  defaultExpanded?: boolean;
  severity?: 'info' | 'warning' | 'error' | 'success';
  sx?: Record<string, unknown>;
}

export const PlaybookCallout: React.FC<PlaybookCalloutProps> = ({
  title,
  doItems,
  dontItems,
  href,
  linkLabel = 'Open playbook',
  summary,
  compact = false,
  defaultExpanded = false,
  severity = 'info',
  sx,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const actions = (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      {compact && (
        <Button color="inherit" size="small" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Hide guidance' : 'Guidance'}
        </Button>
      )}
      {href ? (
        <Button color="inherit" size="small" href={href} target="_blank" rel="noopener noreferrer">
          {linkLabel}
        </Button>
      ) : undefined}
    </Box>
  );

  return (
    <Alert
      severity={severity}
      variant={compact ? 'outlined' : 'standard'}
      sx={{
        ...(compact
          ? {
              py: 0.5,
              '& .MuiAlert-message': { width: '100%' },
              '& .MuiAlert-action': { alignItems: 'center', pr: 1, py: 0.5 },
            }
          : {}),
        ...sx,
      }}
      action={actions}
    >
      {compact ? (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {summary && (
            <Typography variant="body2" color="text.secondary">
              {summary}
            </Typography>
          )}
          <Collapse in={expanded}>
            <Box component="div" sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1 }}>
              <Box component="div" sx={{ minWidth: 220 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Do
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                  {doItems.map((item) => (
                    <li key={item}>
                      <Typography variant="body2">{item}</Typography>
                    </li>
                  ))}
                </Box>
              </Box>
              <Box component="div" sx={{ minWidth: 220 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Don&apos;t
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                  {dontItems.map((item) => (
                    <li key={item}>
                      <Typography variant="body2">{item}</Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            </Box>
          </Collapse>
        </Box>
      ) : (
        <>
          <AlertTitle>{title}</AlertTitle>
          <Box component="div" sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box component="div" sx={{ minWidth: 220 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Do
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {doItems.map((item) => (
                  <li key={item}>
                    <Typography variant="body2">{item}</Typography>
                  </li>
                ))}
              </Box>
            </Box>
            <Box component="div" sx={{ minWidth: 220 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Don&apos;t
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {dontItems.map((item) => (
                  <li key={item}>
                    <Typography variant="body2">{item}</Typography>
                  </li>
                ))}
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Alert>
  );
};
